import { HttpClient, HttpHeaders } from '@angular/common/http';
import { EventEmitter, Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, retry } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { CookieService, CookieOptions } from 'ngx-cookie';


export interface ApiResponse<T> {
  statusCode: string;
  responseData: T;
  //license?: string; // No need to load this
}

export interface UserInfo {
  premium: boolean;
  premiumUntil: number;
  totalPoints: number;
  totalLocatedTowers: number;
  id: number;
  userName: string;
  totalCells: number;
  registered: number;
  passwordUpdated: number;
}

export interface MNCData {
  countryID: number;
  providerID: number;
  providerName: string;
  visible: boolean;
}

export interface Provider {
  countryID: number;
  providerID: number;
  visible: boolean;
}

export interface RATData {
  Provider: Provider;
  RadioTech: string;
}

export interface LastGenerated {
  lastUpdated: number;
}

export interface BandData {
  bandName: string;
  startDownlinkFrequency: number;
  endDownlinkFrequency: number;
  modulation: string;
  bandNumber: number;
}

export interface FrequencyData {
  Frequency: number;
  MCC: number;
  bandName: string;
  bandNumber: number;
  startFrequencyDLNumber: number;
  endFrequencyDLNumber: number;
  modulation: string;
  netType: string;
  possibleBandwidth: string[],
  region: string;
  rxFrequency: number;
  txFrequency: number;
}

export interface TowerInformation {
  Provider: Provider;
  RAT: string;
  RATSubType: string;
  siteID: string;
  regionID: string;
  latitude: number;
  longitude: number;
  visible: boolean;
  firstseendate: number;
  lastseendate: number;
  channels: number[];
  bandNumbers: number[];
  towerMover: number;
  bandwidthData: boolean;
  frequencyData: boolean;
  cells: Cells;
  towerAttributes: TowerAttributes;


  cell_attributes?: CellAttribute[];
}

export interface Cells {
  [key: string]: CellInfo;
}

export interface CellInfo {
  PSC: number;
  Signal: number;
  SubSystem: string;
  BSIC: number;
  FirstSeen: number;
  Sector: number;
  PCI: number;
  LastSeen: number;
  Bearing: number;
  Bandwidth?: number;
}

export interface TowerAttributes {
  TOWER_MOVER_ID: number;
  TOWER_CONTRIBUTORS: number[];
}

export interface Towermoverid {
  uid: string;
  username: string;
}

export interface TowerData {
  RAT: string;
  RATSubType: string;
  siteID: string;
  regionID: string;
  latitude: number;
  longitude: number;
  visible: boolean;
  firstseendate: any;
  lastseendate: any;
  channels: any[];
  bandNumbers: number[];
  towerMover: number;
  bandwidthData: boolean;
  frequencyData: boolean;
  cells: any;
}


/* export interface TowerCoverageData {
  [cell: number]: L.LatLngTuple[];
}
 */

export interface User {
  uid: string;
}

export interface ARFCN {
  uplink: number;
  downlink: number;
  ARFCN: string;
  name: string;
  modulation: string;
  bandNumber: number;
}

export interface EstimatedBand {
  Name: string;
  bandNumber: number;
  EstimatedBandwidth: number;
  band: number;
  Notes: string;
}

export interface EstimatedBandInfo {
  Name: string;
  Frequency: number;
  Modulation: string;
}


export interface CellAttribute {
  [key: string]: CellAttributes;
}

export interface CellAttributes {
  LTE_MIMO?: string;
  LTE_CA_ADD_EARFCN?: string;
  LTE_CA_ADD_BW?: string;
  LTE_CA_BAND?: string;
  LTE_CA_UPLINK?: string;
  LTE_CA_DOWNLINK?: string;
  LTE_CA_ADD_PCI?: string;
  LTE_CA_ADD2_EARFCN?: string;
  LTE_CA_ADD2_BW?: string;
  LTE_CA2_BAND?: string;
  LTE_CA2_UPLINK?: string;
  LTE_CA2_DOWNLINK?: string;
  LTE_CA_ADD2_PCI?: string;
}

export interface CountryNetworks {
  [key: string]: CountryNetwork[];
}

export interface CountryNetwork {
  countryID: number;
  providerID: number;
  providerName: string;
  visible: boolean;
}

export interface LatLong {
  latitude: string;
  longitude: string;
}

export interface CountryAndMCCs {
  name: string;
  MCCs: number[];
  latitude: string;
  longitude: string;
}

export interface LoginResponse {
  sessionID: string;
  loginResponseCode: 'OKAY' | 'NEED_RECAPTCHA';
}

export interface LoginCheckResponse {
  loginCheckResponseCode: 'LOGGEDIN' | 'NOTLOGGEDIN';
  extraHTML: string;
}

export type ApiResponseOkay = 'OKAY' | 'ERROR';

@Injectable({
  providedIn: 'root'
})


export class ApiServiceService {

  private apiUrl = environment.api_url;
  private isLoggedInCache: boolean = false;
  public loginChanged$: EventEmitter<boolean>;
  public userInfoChanged$: EventEmitter<UserInfo>;

  headers: HttpHeaders = new HttpHeaders();

  constructor(
    private http: HttpClient,
    private cookieService: CookieService
  ) {
    this.loginChanged$ = new EventEmitter();
    this.userInfoChanged$ = new EventEmitter();
  }

  


  doLogin(username: string, password: string, captchaCode: string): Observable<ApiResponse<LoginResponse>> {
    return this.doApiPost(this.apiUrl + '/login', {
      User: username,
      Pass: password,
      code: captchaCode
    });
  }

  isLoggedIn(): boolean {
    return this.isLoggedInCache;
  }

  doCheckLogin(): Observable<ApiResponse<LoginCheckResponse>> {
    return this.doApiGet(this.apiUrl + '/getIsLoggedIn', true)
  }

  doGetUserInfo(uid: number = null): Observable<ApiResponse<UserInfo>> {
    return this.doApiGet(this.apiUrl + '/getUserInfo', true);
  }

  doLogOut(): Observable<ApiResponse<any>> {
    return this.doApiGet(this.apiUrl + '/logOff', true)
  }

  doGetFrequency(channel: number, rat: string, mcc: number, bandClass: number): Observable<ApiResponse<FrequencyData>> {
    return this.doApiGet(this.apiUrl + '/getFrequency?Channel=' + channel + '&RAT=' + rat + '&MCC=' + mcc + '&Bandclass=' + bandClass)
  }

  doGetAllNetworks(): Observable<ApiResponse<CountryNetworks>> {
    return this.doApiGet(this.apiUrl + '/getAllNetworks');
  }

  doPostRecaptcha(captchaCode: string, channel: string): Observable<ApiResponseOkay> {
    return this.doApiPost2(this.apiUrl + '/recaptcha', {
      code: captchaCode,
      web: channel
    })
  }

  doApiGet<T>(url: string, withCredentials: boolean = false): Observable<T> {
    return this.http.get<T>(url, { withCredentials: withCredentials, headers: this.headers })
      .pipe(
        catchError(this.handleError())
      );
  }

  doApiPost(url: string, fields: object): Observable<ApiResponse<any>> {
    const body: FormData = new FormData();
    for (var key in fields) {
      body.set(key, fields[key]);
    }
    
    var headers = new Headers();
    headers.append('Content-Type', 'application/x-www-form-urlencoded');
    return this.http.post<any>(url, body, {withCredentials: true, headers: this.headers})
    .pipe(
      catchError(this.handleError())
    );
  }

  doApiPost2(url: string, fields: object): Observable<ApiResponseOkay> {
    const body: FormData = new FormData();
    for (var key in fields) {
      body.set(key, fields[key]);
    }
    
    var headers = new Headers();
    headers.append('Content-Type', 'application/x-www-form-urlencoded');
    return this.http.post<any>(url, body, {withCredentials: true, headers: this.headers})
    .pipe(
      catchError(this.handleError())
    );
  }

  public setCookie(name: string, value: string, days: number) {
    let cookieOpts: CookieOptions = {};
    if (days) {
      var date = new Date();
      date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
      cookieOpts.expires = date;
    }
    this.cookieService.put(name, value, cookieOpts);
  }

  public getCookie(name: string): string {
    return this.cookieService.get(name);
  }

  public eraseCookie(name: string) {
    this.cookieService.remove(name);
  }

 /**
   * Handle Http operation that failed.
   * Let the app continue.
   * @param operation - name of the operation that failed
   * @param result - optional value to return as the observable result
   */
  private handleError() {
    return (error: any): Observable<any> => {

      // TODO: send the error to remote logging infrastructure
      console.error(error); // log to console instead

      return throwError(
        'Something bad happened; please try again later.');
    };
  }

  /** Log a ApiService message with the MessageService */
  private log(message: string) {
    console.log(`ApiService: ${message}`);
  }

}

/*
Copyright Google LLC. All Rights Reserved.
Use of this source code is governed by an MIT-style license that
can be found in the LICENSE file at http://angular.io/license
*/